// Capa de datos — abstrae Supabase para toda la app
import { supabase } from './supabase'

// ─── CAMPAIGNS ───────────────────────────────────────────────────────────────

export async function getCampaigns(userId) {
  const { data, error } = await supabase
    .from('campaigns')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
  if (error) throw error
  return data
}

export async function createCampaign(userId, campaign) {
  const { data, error } = await supabase
    .from('campaigns')
    .insert({ user_id: userId, ...campaign })
    .select()
    .single()
  if (error) throw error
  return data
}

export async function updateCampaign(id, updates) {
  const { data, error } = await supabase
    .from('campaigns')
    .update(updates)
    .eq('id', id)
    .select()
    .single()
  if (error) throw error
  return data
}

export async function deleteCampaign(id) {
  const { error } = await supabase
    .from('campaigns')
    .delete()
    .eq('id', id)
  if (error) throw error
}

// ─── COPY HISTORY ─────────────────────────────────────────────────────────────

export async function getCopyHistory(userId) {
  const { data, error } = await supabase
    .from('copy_history')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(20)
  if (error) throw error
  return data
}

export async function saveCopy(userId, { platform, objective, style, variations }) {
  const { data, error } = await supabase
    .from('copy_history')
    .insert({ user_id: userId, platform, objective, style, variations })
    .select()
    .single()
  if (error) throw error
  return data
}

export async function deleteCopy(id) {
  const { error } = await supabase
    .from('copy_history')
    .delete()
    .eq('id', id)
  if (error) throw error
}

// ─── AGENT CONFIG ─────────────────────────────────────────────────────────────

export async function getAgentConfig(userId) {
  // .maybeSingle() devuelve null si no hay fila — sin HTTP 406 y sin throw.
  // .single() tiraba 406 en cada check (el user aún no tiene agent_config)
  // lo cual aparecía como error en la consola del navegador.
  const { data, error } = await supabase
    .from('agent_config')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle()
  if (error) throw error
  return data
}

export async function saveAgentConfig(userId, config) {
  const { data, error } = await supabase
    .from('agent_config')
    .upsert({ user_id: userId, ...config, updated_at: new Date().toISOString() })
    .select()
    .single()
  if (error) throw error
  return data
}

// ─── CONNECTIONS ──────────────────────────────────────────────────────────────

export async function getConnections(userId) {
  const { data, error } = await supabase
    .from('connections')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle()
  if (error) throw error
  return data
}

export async function saveConnections(userId, updates) {
  const { data, error } = await supabase
    .from('connections')
    .upsert({ user_id: userId, ...updates, updated_at: new Date().toISOString() })
    .select()
    .single()
  if (error) throw error
  return data
}

// ─── GENERATED IMAGES ─────────────────────────────────────────────────────────

export async function getGeneratedImages(userId) {
  const { data, error } = await supabase
    .from('generated_images')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(50)
  if (error) throw error
  return data
}

export async function saveGeneratedImage(userId, { url, prompt, style, format, model }) {
  const payload = { user_id: userId, url, prompt, style, format }
  if (model) payload.model = model
  const { data, error } = await supabase
    .from('generated_images')
    .insert(payload)
    .select()
    .single()
  // Fallback robusto: si la columna `model` aún no existe en el schema del
  // user (migración pendiente), reintentamos sin ella. Supabase/PostgREST da
  // varios códigos/mensajes para el mismo fallo según la capa:
  //   - '42703' (Postgres undefined_column)
  //   - 'PGRST204' (PostgREST schema cache miss)
  //   - mensajes del estilo "Could not find the 'model' column..."
  // Por eso el match es por código Y por texto del mensaje.
  const msg = String(error?.message || '').toLowerCase()
  const isUnknownColumn = error && (
    error.code === '42703' ||
    error.code === 'PGRST204' ||
    msg.includes("could not find the 'model'") ||
    msg.includes('column "model"') ||
    (msg.includes("'model'") && msg.includes('schema cache'))
  )
  if (isUnknownColumn && model) {
    const retry = await supabase
      .from('generated_images')
      .insert({ user_id: userId, url, prompt, style, format })
      .select()
      .single()
    if (retry.error) throw retry.error
    return retry.data
  }
  if (error) throw error
  return data
}

export async function deleteGeneratedImage(id) {
  const { error } = await supabase
    .from('generated_images')
    .delete()
    .eq('id', id)
  if (error) throw error
}

// ─── PROFILE ──────────────────────────────────────────────────────────────────

export async function getProfile(userId) {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .maybeSingle()
  if (error) throw error
  return data
}

export async function updateProfile(userId, updates) {
  const { data, error } = await supabase
    .from('profiles')
    .update(updates)
    .eq('id', userId)
    .select()
    .single()
  if (error) throw error
  return data
}
